import { ingestFile } from "../ingestion/ingestFile";
import { extractGeneralText } from "../paddle/paddleTextClient";
import { extractPdfText } from "../paddle/paddlePdfClient";
import {
  averageLineConfidence,
  linesToRawText,
} from "../paddle/paddleTextAdapter";
import { needsTableExtraction } from "../paddle/documentTypeRouting";
import { extractTableStructure } from "../paddle/paddleStructureClient";
import { parseTableHtml } from "../paddle/tableHtmlParser";

const emptyTableResult = { success: true, tables: [], textRegions: [] };

const isPdfFile = (file) =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

/**
 * @param {File} file
 * @param {string} [documentType]
 * @param {{ onProgress?: (payload: object) => void }} [options]
 * @returns {Promise<import("../types").ExtractionResult>}
 */
export async function runExtraction(file, documentType, options = {}) {
  const { onProgress } = options;
  const start = Date.now();
  const runTableExtraction = needsTableExtraction(documentType);

  const { pages } = await ingestFile(file, { renderPdfImages: runTableExtraction });

  const keyValueFields = [];
  const tables = [];
  const textParts = [];
  const ocrLines = [];
  const confidences = [];
  let needsReview = false;

  /** @type {Map<number, Array>|null} */
  let pdfLinesByPage = null;
  let pdfBatchError = null;

  if (isPdfFile(file)) {
    const pagesNeedingOcr = pages.filter((page) => page.text === null);
    if (pagesNeedingOcr.length > 0) {
      try {
        const response = await extractPdfText(file);
        if (response.success && Array.isArray(response.pages)) {
          pdfLinesByPage = new Map(
            response.pages.map((entry) => [
              entry.page,
              Array.isArray(entry.lines) ? entry.lines : [],
            ]),
          );
        } else {
          pdfBatchError = response.error || "PDF OCR failed.";
        }
      } catch (reason) {
        pdfBatchError =
          reason instanceof Error ? reason.message : "PDF OCR service unavailable.";
      }
    }
  }

  const emitProgress = (stage) => {
    if (!onProgress) return;
    onProgress({
      stage,
      rawText: textParts.join("\n\n---\n\n"),
      ocrLines: [...ocrLines],
      keyValueFields: [...keyValueFields],
      tables: [...tables],
      needsReview,
      pageCount: pages.length,
    });
  };

  const applyOcrLines = (page, lines) => {
    if (!lines.length) {
      return;
    }

    const rawText = linesToRawText(lines);
    textParts.push(rawText);
    ocrLines.push(...lines);
    confidences.push(averageLineConfidence(lines));
  };

  for (const page of pages) {
    if (page.text !== null) {
      textParts.push(page.text);
      confidences.push(1);
      continue;
    }

    let textResult;
    let paddleResult = { status: "fulfilled", value: emptyTableResult };

    if (pdfLinesByPage) {
      if (!pdfLinesByPage.has(page.pageNumber)) {
        needsReview = true;
        keyValueFields.push({
          label: `Page ${page.pageNumber} – Field Extraction`,
          value: "PDF OCR result missing for this page.",
          confidence: 0,
        });
      } else {
        applyOcrLines(page, pdfLinesByPage.get(page.pageNumber));
      }
      textResult = { status: "fulfilled", value: { success: true } };
    } else if (pdfBatchError) {
      needsReview = true;
      keyValueFields.push({
        label: `Page ${page.pageNumber} – Field Extraction`,
        value: pdfBatchError,
        confidence: 0,
      });
      textResult = { status: "rejected", reason: new Error(pdfBatchError) };
    } else if (!page.image) {
      continue;
    } else {
      try {
        const textResponse = await extractGeneralText(page.image);
        textResult = { status: "fulfilled", value: textResponse };
      } catch (reason) {
        textResult = { status: "rejected", reason };
      }

      if (textResult.status === "fulfilled") {
        const response = textResult.value;
        if (!response.success || !response.lines?.length) {
          needsReview = true;
          keyValueFields.push({
            label: `Page ${page.pageNumber} – Field Extraction`,
            value: response.error || "PaddleOCR text extraction returned no lines.",
            confidence: 0,
          });
        } else {
          applyOcrLines(page, response.lines);
        }
      } else {
        console.error(
          `PaddleOCR text extraction failed for page ${page.pageNumber}:`,
          textResult.reason,
        );
        needsReview = true;
        keyValueFields.push({
          label: `Page ${page.pageNumber} – Field Extraction`,
          value:
            textResult.reason instanceof Error
              ? textResult.reason.message
              : "PaddleOCR text extraction service unavailable.",
          confidence: 0,
        });
      }
    }

    emitProgress("text");

    if (runTableExtraction && page.image) {
      try {
        const tableResponse = await extractTableStructure(page.image);
        paddleResult = { status: "fulfilled", value: tableResponse };
      } catch (reason) {
        paddleResult = { status: "rejected", reason };
      }
    }

    if (runTableExtraction && page.image) {
      if (paddleResult.status === "fulfilled") {
        const result = paddleResult.value;
        if (!result.success || result.tables.length === 0) {
          needsReview = true;
        }
        for (const table of result.tables) {
          const parsed = parseTableHtml(
            table.html,
            pages.length > 1
              ? `Page ${page.pageNumber} – Detail of Marks Obtained`
              : "Detail of Marks Obtained",
          );
          if (parsed) tables.push(parsed);
        }
      } else {
        console.error(
          `PP-Structure table extraction failed for page ${page.pageNumber}:`,
          paddleResult.reason,
        );
        needsReview = true;
        keyValueFields.push({
          label: `Page ${page.pageNumber} – Table Extraction`,
          value:
            paddleResult.reason instanceof Error
              ? paddleResult.reason.message
              : "Table extraction service unavailable.",
          confidence: 0,
        });
      }
      emitProgress("table");
    }
  }

  const fullText = textParts.join("\n\n---\n\n");
  const overallConfidence = confidences.length
    ? confidences.reduce((sum, c) => sum + c, 0) / confidences.length
    : 0;

  if (overallConfidence < 0.6) needsReview = true;

  const providerLabel = runTableExtraction
    ? "PaddleOCR text + PaddleOCR PP-Structure (tables)"
    : "PaddleOCR text";

  return {
    documentType: file.type.startsWith("image/") ? "Image" : "Document",
    provider: providerLabel,
    processingTimeMs: Date.now() - start,
    pageCount: pages.length,
    file: { name: file.name, size: file.size, type: file.type },
    fields: keyValueFields,
    keyValueFields,
    tables,
    ocrLines: ocrLines.length ? ocrLines : undefined,
    rawText: fullText || undefined,
    needsReview,
  };
}
